import { useEffect, useState } from 'react';
import { Image, Platform, Pressable, Text, View } from 'react-native';
import { CheckCircle2, FileUp, MessageCircle, QrCode, Sparkles } from 'lucide-react-native';

import { Button, Sheet, TextField, type SelectOption } from '@/components/ui';
import { rendirPetshop } from '@/services/firebase/petshops';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs } from '@/utils/format';
import { enviarComprobanteWhatsApp, esMac } from '@/utils/whatsappComprobante';
import type { PetshopId } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  petshop: PetshopId;
  petshopCorto: string;
  qrUrl?: string | null;
  telefono?: string | null;
  dueno?: string | null;
  total: number;
  cantidad: number;
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const METODOS: SelectOption[] = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'qr', label: 'QR' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'otro', label: 'Otro' },
];

interface ArchivoSeleccionado {
  blob: Blob;
  name: string;
  size: number;
}

export function RendirSheet({ visible, onClose, petshop, petshopCorto, qrUrl, telefono, dueno, total, cantidad }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const [metodo, setMetodo] = useState<string | null>('qr');
  const [notas, setNotas] = useState('');
  const [archivo, setArchivo] = useState<ArchivoSeleccionado | null>(null);
  const [saving, setSaving] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [comprobanteUrl, setComprobanteUrl] = useState<string | null>(null);
  const [waHint, setWaHint] = useState<string | null>(null);
  const [waRetry, setWaRetry] = useState<null | (() => void)>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setMetodo('qr');
    setNotas('');
    setArchivo(null);
    setSaving(false);
    setEnviado(false);
    setComprobanteUrl(null);
    setWaHint(null);
    setWaRetry(null);
    setError(null);
  }, [visible]);

  const enviarWhatsapp = () => {
    if (!telefono) return;
    const ahora = new Date();
    const periodo = `${MESES[ahora.getMonth()]} ${ahora.getFullYear()}`;
    const caption = `Hola${dueno ? ` ${dueno}` : ''}, rendición de ${petshopCorto} — ${periodo}: ${cantidad} ${cantidad === 1 ? 'venta' : 'ventas'}, total ${formatBs(total)}.`;
    const teclas = esMac() ? 'Cmd+V' : 'Ctrl+V';
    setWaHint(null);
    setWaRetry(null);
    enviarComprobanteWhatsApp({
      imageUrl: comprobanteUrl,
      numero: telefono,
      caption,
      onEstado: (e) => {
        if (e.tipo === 'copiado') {
          setWaHint(`Foto copiada. En el chat de WhatsApp pegá con ${teclas} y apretá Enter.`);
          setWaRetry(null);
        } else if (e.tipo === 'popupBloqueado') {
          setWaHint('Tu navegador bloqueó la pestaña. Tocá "Abrir WhatsApp Web".');
          setWaRetry(() => e.abrir);
        } else if (e.tipo === 'descargado') {
          setWaHint('Se descargó el comprobante. Abrí WhatsApp Web y arrastrá la foto al chat.');
          setWaRetry(() => e.abrir);
        } else {
          setWaHint(null);
          setWaRetry(null);
        }
      },
    });
  };

  const onPickFile = () => {
    if (Platform.OS !== 'web') {
      setError('La carga de comprobante desde móvil llega en Fase 3 (image-picker).');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 15 * 1024 * 1024) {
        setError('Archivo muy pesado (máx 15 MB).');
        return;
      }
      setArchivo({ blob: file, name: file.name, size: file.size });
    };
    input.click();
  };

  const onConfirm = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    setSaving(true);
    try {
      const res = await rendirPetshop({
        petshop,
        metodoTransferencia: (metodo as any) ?? 'qr',
        comprobante: archivo ? { file: archivo.blob, filename: archivo.name } : undefined,
        notas: notas.trim() || undefined,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      setComprobanteUrl((res as any)?.comprobanteUrl ?? null);
      setEnviado(true);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo rendir.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title={`Rendir a ${petshopCorto}`}
      subtitle={`${cantidad} ventas · ${formatBs(total)}`}
      footer={
        enviado ? null : (
          <>
            <Button label="Cancelar" variant="ghost" onPress={onClose} />
            <Button
              label={`Confirmar rendición · ${formatBs(total)}`}
              onPress={onConfirm}
              loading={saving}
              variant="success"
            />
          </>
        )
      }
    >
      {enviado ? (
        <View style={{ alignItems: 'center', paddingVertical: 18, gap: 10 }}>
          <View
            style={{
              width: 64,
              height: 64,
              borderRadius: 32,
              backgroundColor: colors.success[50],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle2 size={32} color={colors.success[600]} />
          </View>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 22, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            ¡Rendición lista!
          </Text>
          <Text style={{ color: colors.ink[500], fontSize: 13, textAlign: 'center' }}>
            Las {cantidad} ventas quedaron archivadas y el período empieza desde cero.
          </Text>
          <View style={{ alignSelf: 'stretch', gap: 8, marginTop: 8 }}>
            {telefono ? (
              <>
                <Button
                  label={`Enviar comprobante por WhatsApp a ${petshopCorto}`}
                  variant="success"
                  iconLeft={<MessageCircle size={16} color={colors.white} />}
                  onPress={enviarWhatsapp}
                />
                {waHint ? (
                  <View style={{ padding: 10, borderRadius: 12, backgroundColor: colors.success[50] }}>
                    <Text style={{ fontSize: 12.5, color: colors.success[600], fontWeight: typography.weights.semibold, textAlign: 'center' }}>
                      {waHint}
                    </Text>
                  </View>
                ) : null}
                {waRetry ? (
                  <Button label="Abrir WhatsApp Web" variant="ghost" onPress={() => waRetry?.()} />
                ) : null}
              </>
            ) : (
              <Text style={{ fontSize: 12, color: colors.ink[400], textAlign: 'center' }}>
                Agregá el teléfono del petshop (en editar) para poder mandar el comprobante por WhatsApp.
              </Text>
            )}
            <Button label="Cerrar" variant="ghost" onPress={onClose} />
          </View>
        </View>
      ) : (
        <>
          {qrUrl ? (
            <View style={{ alignItems: 'center', gap: 8, padding: 14, borderRadius: radius['2xl'], borderWidth: 1, borderColor: colors.ink[100], backgroundColor: colors.crema[50] }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <QrCode size={15} color={colors.magenta[700]} />
                <Text style={{ fontSize: 12, fontWeight: typography.weights.bold, color: colors.magenta[700], letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Transferí a {petshopCorto}
                </Text>
              </View>
              <Image source={{ uri: qrUrl }} style={{ width: 190, height: 190, borderRadius: 12, backgroundColor: colors.white }} /* @ts-ignore */ resizeMode="contain" />
            </View>
          ) : null}
          <View
            style={{
              padding: 18,
              borderRadius: radius['2xl'],
              backgroundColor: colors.magenta[700],
              // @ts-ignore web only
              backgroundImage: `linear-gradient(135deg, ${colors.magenta[800]} 0%, ${colors.magenta[600]} 100%)`,
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 1.4, textTransform: 'uppercase' }}>
              Total a rendir
            </Text>
            <Text style={{ color: colors.white, fontFamily: typography.display.fontFamily, fontSize: 36, fontWeight: typography.weights.bold, letterSpacing: -0.5 }}>
              {formatBs(total)}
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{cantidad} ventas del período</Text>
          </View>

          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>Método de transferencia al dueño</Text>
            {Platform.OS === 'web' ? (
              // @ts-ignore web-only native select (dropdown, no abre otra pantalla)
              <select
                value={metodo ?? 'qr'}
                onChange={(e: any) => setMetodo(e.target.value)}
                style={{
                  height: 48,
                  padding: '0 12px',
                  borderRadius: 14,
                  border: `1.5px solid ${colors.ink[200]}`,
                  background: colors.white,
                  color: colors.ink[900],
                  fontSize: 14,
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  outline: 'none',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                {METODOS.map((m) => (
                  // @ts-ignore web-only option
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            ) : (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {METODOS.map((m) => {
                  const on = metodo === m.value;
                  return (
                    <Pressable
                      key={m.value}
                      onPress={() => setMetodo(m.value)}
                      style={{ paddingHorizontal: 14, paddingVertical: 9, borderRadius: 999, borderWidth: 1.5, borderColor: on ? colors.magenta[600] : colors.ink[200], backgroundColor: on ? colors.magenta[600] : colors.white }}
                    >
                      <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: on ? colors.white : colors.ink[700] }}>{m.label}</Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </View>

          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>
              Comprobante (opcional pero recomendado)
            </Text>
            <Pressable
              onPress={onPickFile}
              style={({ hovered }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                padding: 14,
                borderRadius: radius.xl,
                borderWidth: 1.5,
                borderStyle: 'dashed',
                borderColor: archivo ? colors.success[500] : colors.magenta[300],
                backgroundColor: archivo ? colors.success[50] : hovered ? colors.magenta[50] : colors.crema[50],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <FileUp size={18} color={archivo ? colors.success[600] : colors.magenta[700]} />
              <View style={{ flex: 1 }}>
                {archivo ? (
                  <>
                    <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.success[600] }}>
                      Comprobante adjunto
                    </Text>
                    <Text style={{ fontSize: 11, color: colors.ink[500] }} numberOfLines={1}>
                      {archivo.name} · {(archivo.size / 1024).toFixed(0)} KB
                    </Text>
                  </>
                ) : (
                  <>
                    <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[800] }}>
                      Adjuntar imagen o PDF
                    </Text>
                    <Text style={{ fontSize: 11, color: colors.ink[500] }}>
                      Captura de la transferencia · máx 15 MB
                    </Text>
                  </>
                )}
              </View>
              {archivo ? (
                <Text style={{ color: colors.success[600], fontSize: 11, fontWeight: typography.weights.bold }}>
                  Cambiar
                </Text>
              ) : null}
            </Pressable>
          </View>

          <TextField
            label="Notas (opcional)"
            placeholder="Ej: transferido al BNB cuenta 4002... · 30/05/2026"
            value={notas}
            onChangeText={setNotas}
            multiline
          />

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 12, backgroundColor: colors.crema[50] }}>
            <Sparkles size={14} color={colors.magenta[700]} />
            <Text style={{ flex: 1, fontSize: 12, color: colors.ink[600] }}>
              Una vez confirmada, las {cantidad} ventas se archivan en el histórico y el contador del mes se reinicia.
            </Text>
          </View>

          {error ? (
            <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
              <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
            </View>
          ) : null}
        </>
      )}
    </Sheet>
  );
}
